import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings2 } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import HelpDot from '@/components/ui/HelpDot';
import type { Project } from '@/hooks/useProject';

const FIELD_CLS =
  'mt-1 w-full max-w-md rounded-md border border-border bg-elevated px-2.5 py-2 text-[13px] outline-none focus:border-primary';

/**
 * Settings that live on the PROJECT and are inherited by every production it runs.
 *
 * These were previously mixed into the launch panel alongside per-run choices, which
 * made it unclear that changing them here changes them for good.
 */
export default function ProjectSettingsCard({
  project,
  onPatch,
}: {
  project: Project;
  onPatch: (body: Record<string, unknown>) => Promise<void>;
}) {
  const { t } = useTranslation(['dashboard', 'common']);
  const isFootage = (project.production_type || 'generative') === 'footage';

  const [aspectRatio, setAspectRatio] = useState(project.aspect_ratio || '16:9');
  const [collectionId, setCollectionId] = useState(project.iconik_collection_id || '');
  const [voicePolicy, setVoicePolicy] = useState(project.voice_policy || 'default');
  const [saved, setSaved] = useState<string | null>(null);

  const flashSaved = (field: string) => {
    setSaved(field);
    setTimeout(() => setSaved(null), 2000);
  };

  const changeAspect = async (ar: string) => {
    setAspectRatio(ar);
    await onPatch({ aspect_ratio: ar });
    flashSaved('aspect');
  };

  const changeVoicePolicy = async (policy: string) => {
    setVoicePolicy(policy);
    await onPatch({ voice_policy: policy });
    flashSaved('voice');
  };

  const saveCollectionId = async () => {
    const val = collectionId.trim();
    if (val === (project.iconik_collection_id || '')) return;
    await onPatch({ iconik_collection_id: val });
    flashSaved('collection');
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Settings2 className="h-4 w-4" />
          {t('settings.title')}
        </CardTitle>
        <CardDescription>{t('settings.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <label className="block">
          <span className="flex items-center gap-1.5 text-[12.5px] font-medium">
            {t('launch.aspect')}
            <HelpDot>{t('launch.aspectHelp')}</HelpDot>
            {saved === 'aspect' && <span className="text-[11px] text-success">{t('launch.collectionSaved')}</span>}
          </span>
          <select className={FIELD_CLS} value={aspectRatio} onChange={(e) => changeAspect(e.target.value)}>
            <option value="16:9">{t('launch.aspect169')}</option>
            <option value="9:16">{t('launch.aspect916')}</option>
          </select>
        </label>

        {!isFootage && (
          <label className="block">
            <span className="flex items-center gap-1.5 text-[12.5px] font-medium">
              {t('settings.voicePolicy')}
              <HelpDot>{t('settings.voicePolicyHelp')}</HelpDot>
              {saved === 'voice' && <span className="text-[11px] text-success">{t('launch.collectionSaved')}</span>}
            </span>
            <select className={FIELD_CLS} value={voicePolicy} onChange={(e) => changeVoicePolicy(e.target.value)}>
              <option value="default">{t('settings.voicePolicyDefault')}</option>
              <option value="no_voiceover">{t('settings.voicePolicyNoVoiceover')}</option>
              <option value="none">{t('settings.voicePolicyNone')}</option>
            </select>
          </label>
        )}

        {isFootage && (
          <label className="block">
            <span className="flex items-center gap-1.5 text-[12.5px] font-medium">
              {t('launch.collection')}
              <HelpDot>{t('launch.collectionHelp')}</HelpDot>
              {saved === 'collection' && <span className="text-[11px] text-success">{t('launch.collectionSaved')}</span>}
            </span>
            <input
              className={`${FIELD_CLS} font-mono`}
              value={collectionId}
              placeholder={t('launch.collectionPlaceholder')}
              onChange={(e) => setCollectionId(e.target.value)}
              onBlur={saveCollectionId}
              onKeyDown={(e) => e.key === 'Enter' && saveCollectionId()}
            />
          </label>
        )}
      </CardContent>
    </Card>
  );
}
